import { APP_INITIALIZER, ErrorHandler, Injector, NgModule, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { Router, RouteReuseStrategy } from '@angular/router';
import * as Sentry from '@sentry/angular';

import {
  IonApp,
  IonicRouteStrategy,
  IonRouterOutlet,
  provideIonicAngular,
} from '@ionic/angular/standalone';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { createErrorHandler } from './services/error-logging/app-error-handler';
import {
  ApiModule,
  Configuration,
} from './api/client';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { XsrfInterceptor } from './services/security/xsrf-interceptor.service';
import { AuthInterceptor } from './services/security/auth-interceptor.service';
import { ApiConfiguration } from './api/api-configuration';
import { AppCompatibilityInterceptor } from './services/security/app-compatibility-interceptor.service';
import { TestModeInterceptor } from './services/security/test-mode-interceptor.service';
import { NetworkOfflineInterceptor } from './services/security/network-offline-interceptor.service';
import { DevSimulationInterceptor } from './services/security/dev-simulation-interceptor.service';
import { OAuthModule } from 'angular-oauth2-oidc';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    ApiModule.forRoot(() => {
      const injector = AppModule.injector;
      return injector.get(ApiConfiguration);
    }),
    IonApp,
    IonRouterOutlet,
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: [
          'sso.caldevo.app/admin/v1',
          'sso.caldevo.app/management/v1',
          'sso.caldevo.app/auth/v1/',
        ],
        sendAccessToken: true,
      },
    }),
  ],
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: Configuration, useExisting: ApiConfiguration },
    { provide: HTTP_INTERCEPTORS, useClass: DevSimulationInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: NetworkOfflineInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: XsrfInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppCompatibilityInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: TestModeInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
    provideIonicAngular({ mode: 'ios' }),
    {
      provide: ErrorHandler,
      useFactory: createErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  static injector: Injector;

  constructor() {
    const injector = inject(Injector);

    AppModule.injector = injector;
  }
}
